import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { getDynamicTranslation } from '@monorepo/client-common';
import { ReleaseData } from '@monorepo/types';
import {
  QUERY_CONFIGS,
  QueryConfigKeys,
} from '@hub/utils/react-query/query-config';
import {
  fetchReleaseById,
  fetchReleases,
} from '@hub/utils/react-query/query-functions';
import { QUERY_KEYS } from '@hub/utils/react-query/query-keys';
import { ExtractFnReturnType } from '@hub/utils/react-query/react-query';
import { useQuery } from '@tanstack/react-query';

const CONFIG_KEY = QueryConfigKeys.RELEASE;

type RawRelease = ExtractFnReturnType<typeof fetchReleases>[number];

function toReleaseData(
  release: RawRelease,
  t: TFunction,
  language: string,
): ReleaseData {
  const { _id, releaseDate, ...rest } = release;

  return {
    ...rest,
    releaseId: _id,
    releaseDate: new Date(releaseDate),
    releaseTitle: getDynamicTranslation('releaseTitle', release, t, language),
    descriptions: getDynamicTranslation('descriptions', release, t, language),
    subtitle: getDynamicTranslation('subtitle', release, t, language),
    primaryButtonText: getDynamicTranslation(
      'primaryButtonText',
      release,
      t,
      language,
    ),
    secondaryButtonText: getDynamicTranslation(
      'secondaryButtonText',
      release,
      t,
      language,
    ),
  } as ReleaseData;
}

export const useLast30DaysReleases = (options?: { enabled?: boolean }) => {
  const { t, i18n } = useTranslation();
  const enabled = options?.enabled !== false;

  return useQuery<
    ExtractFnReturnType<typeof fetchReleases>,
    Error,
    ReleaseData[]
  >({
    queryKey: [...QUERY_KEYS[CONFIG_KEY].LAST_30_DAYS, i18n.language],
    queryFn: () => fetchReleases({ since: '30d' }),
    enabled,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    select: (data) =>
      data.map((release) => toReleaseData(release, t, i18n.language)),
  });
};

export const useReleaseById = (id: string | undefined) => {
  const { t, i18n } = useTranslation();

  return useQuery({
    queryKey: [...QUERY_KEYS[CONFIG_KEY].DETAIL(id ?? ''), i18n.language],
    queryFn: () => fetchReleaseById(id!),
    enabled: !!id,
    staleTime: QUERY_CONFIGS[CONFIG_KEY].staleTime,
    select: (data: RawRelease | null): ReleaseData | undefined =>
      data ? toReleaseData(data, t, i18n.language) : undefined,
  });
};
